import React, { useState, useEffect, useRef, useMemo } from "react";
import StoreTableItem from "./storeTableItem.jsx";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import StoreModal from "./storeModal.jsx";
import MarketplaceCSVUpload from "./MarketplaceCSVUpload.jsx";
import SellerManualPayoutModal from "./SellerManualPayoutModal.jsx";
import RequestLicensesModal from "./RequestLicensesModal.jsx";
import { useDispatch } from "react-redux";
import ModalBasic from "../../components/ModalBasic.jsx";
import { archiveStore, approveOnboardSteps, rejectOnboardSteps, getStoreById, getStoreBusinessDocuments, getStripeAccountManagementToken } from "../../api/admin.js";
import { getBusinessDocumentUrl } from "../../api/settings.js";
import { addToast } from "../../store/app/action.js";
import { onboardChecklistItems } from "../../utils/Utils.js";
import { formatPhoneNumber } from "../../utils/Utils.js";
import Badge from "../../components/Badges.jsx";
import { FaCircleCheck, FaCircleXmark, FaClock, FaFilePdf, FaFileWord, FaFileLines, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { ConnectAccountManagement, ConnectComponentsProvider } from "@stripe/react-connect-js";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import config from "@/config";

function ProspectProgressModal({ store, setModalOpen, getMerchants }) {
  const dispatch = useDispatch();
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [storeDetails, setStoreDetails] = useState(null);
  const [businessDocuments, setBusinessDocuments] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingDocumentUrl, setLoadingDocumentUrl] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState({
    businessDetails: false,
    businessDocuments: false,
  });
  const [bankingToken, setBankingToken] = useState(null);
  const [loadingBankingToken, setLoadingBankingToken] = useState(false);
  const [bankingTokenError, setBankingTokenError] = useState(false);

  useEffect(() => {
    if (store?.id) {
      const fetchDetails = async () => {
        setLoadingDetails(true);
        setLoadingDocuments(true);
        try {
          const [storeData, documentsData] = await Promise.all([
            getStoreById(store.id),
            getStoreBusinessDocuments(store.id).catch(() => ({ data: [] })) // Handle if API doesn't exist yet
          ]);
          setStoreDetails(storeData.store || storeData);
          setBusinessDocuments(documentsData.data  || []);
        } catch (error) {
          console.error('Error fetching store details:', error);
          // Fallback to store data we already have
          setStoreDetails(store);
        } finally {
          setLoadingDetails(false);
          setLoadingDocuments(false);
        }
      };
      fetchDetails();
    }
  }, [store?.id]);

  // Stripe Connect instance for banking
  const bankingStripeConnectInstance = useMemo(() => {
    if (!bankingToken) {
      return null;
    }
    return loadConnectAndInitialize({
      publishableKey: config.stripe.pk,
      clientSecret: bankingToken,
    });
  }, [bankingToken]);

  // Reset banking token state when store changes or accordion closes
  useEffect(() => {
    if (!expandedAccordions.bankingInformation) {
      setBankingToken(null);
      setBankingTokenError(false);
      setLoadingBankingToken(false);
    }
  }, [expandedAccordions.bankingInformation]);

  // Reset banking token state when store changes
  useEffect(() => {
    setBankingToken(null);
    setBankingTokenError(false);
    setLoadingBankingToken(false);
  }, [store?.id]);

  // Load banking token when accordion expands
  useEffect(() => {
    if (expandedAccordions.bankingInformation && store?.id && !bankingToken && !loadingBankingToken && !bankingTokenError) {
      const loadToken = async () => {
        setLoadingBankingToken(true);
        setBankingTokenError(false);
        try {
          const response = await getStripeAccountManagementToken(store.id);
          setBankingToken(response.client_secret);
          setBankingTokenError(false);
        } catch (error) {
          console.error('Error loading banking token:', error);
          setBankingTokenError(true);
          dispatch(addToast({
            type: "error",
            message: "Failed to load banking information. Please try again.",
            timer: 5
          }));
        } finally {
          setLoadingBankingToken(false);
        }
      };
      loadToken();
    }
  }, [expandedAccordions.bankingInformation, store?.id, bankingToken, loadingBankingToken, bankingTokenError, dispatch]);

  if (!store) return null;

  const displayStore = storeDetails || store;

  const onboardSteps = store.onboard_steps || {};
  const stepsStatus = onboardSteps.steps || {};
  const overallStatus = onboardSteps.status;
  const totalSteps = onboardChecklistItems.length;
  const completedSteps = Object.values(stepsStatus).filter(status => status === 'completed').length;
  const inProgressSteps = Object.values(stepsStatus).filter(status => status === 'in_progress').length;
  const publishOption = onboardSteps.publish_option || 'N/A';
  const scheduledDate = onboardSteps.scheduled_date || '';
  const scheduledTime = onboardSteps.scheduled_time || '';

  const getStepStatusIcon = (stepKey) => {
    const status = stepsStatus[stepKey];
    if (status === 'completed') {
      return <FaCircleCheck className="w-5 h-5 text-green-600 dark:text-green-400" />;
    } else if (status === 'in_progress') {
      return <FaClock className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />;
    }
    return <FaCircleXmark className="w-5 h-5 text-slate-300 dark:text-slate-600" />;
  };

  const getStepStatusBadge = (stepKey) => {
    const status = stepsStatus[stepKey];
    if (status === 'completed') {
      return <Badge color="green">Completed</Badge>;
    } else if (status === 'in_progress') {
      return <Badge color="yellow">In Progress</Badge>;
    }
    return <Badge color="slate">Not Started</Badge>;
  };

  const handleApproveClick = () => {
    setShowApproveModal(true);
  };

  const handleConfirmApprove = async () => {
    setProcessing(true);
    try {
      await approveOnboardSteps(store.id);
      dispatch(addToast({
        type: "success",
        message: `Onboard steps approved for ${store.name}`,
        timer: 5,
      }));
      getMerchants();
      setShowApproveModal(false);
      setModalOpen(null);
    } catch (error) {
      dispatch(addToast({
        type: "error",
        message: `Failed to approve onboard steps: ${error.message || "Unknown error"}`,
        timer: 5,
      }));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      dispatch(addToast({
        type: "error",
        message: "Please provide a rejection reason",
        timer: 5,
      }));
      return;
    }

    setProcessing(true);
    try {
      await rejectOnboardSteps(store.id, rejectionReason.trim());
      dispatch(addToast({
        type: "success",
        message: `Onboard steps rejected for ${store.name}. User will be notified via email.`,
        timer: 5,
      }));
      getMerchants();
      setShowRejectModal(false);
      setRejectionReason("");
      setModalOpen(null);
    } catch (error) {
      dispatch(addToast({
        type: "error",
        message: `Failed to reject onboard steps: ${error.message || "Unknown error"}`,
        timer: 5,
      }));
    } finally {
      setProcessing(false);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await archiveStore(store.id);
      dispatch(addToast({
        type: "success",
        message: `Successfully archived ${store.name}`,
        timer: 5,
      }));
      getMerchants();
      setModalOpen(null);
    } catch (error) {
      dispatch(addToast({
        type: "error",
        message: `Failed to archive store: ${error.message || "Unknown error"}`,
        timer: 5,
      }));
    } finally {
      setArchiving(false);
    }
  };

  const canApprove = overallStatus === 'submitted';
  const canReject = overallStatus === 'submitted' || overallStatus === 'working';
  const showActions = overallStatus === 'submitted' || overallStatus === 'rejected' || overallStatus === 'working';

  // File icon helper function
  const getFileIcon = (filename, size = "w-6 h-6") => {
    const extension = filename?.split('.').pop().toLowerCase() || "";
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className={`${size} text-red-500`} />;
      case 'doc':
      case 'docx':
        return <FaFileWord className={`${size} text-blue-500`} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
        return <FaFileLines className={`${size} text-green-500`} />;
      default:
        return <FaFileLines className={`${size} text-slate-500`} />;
    }
  };

  const handleViewDocument = async (document) => {
    try {
      setLoadingDocumentUrl(document.id);
      const response = await getBusinessDocumentUrl(document.id);
      const authenticatedUrl = response.url || response.data?.url;
      
      if (authenticatedUrl) {
        window.open(authenticatedUrl, '_blank', 'noopener,noreferrer');
      } else {
        dispatch(addToast({
          type: "error",
          message: "Failed to get document URL",
          timer: 5
        }));
      }
    } catch (error) {
      console.error('Error getting document URL:', error);
      dispatch(addToast({
        type: "error",
        message: "Failed to open document. Please try again.",
        timer: 5
      }));
    } finally {
      setLoadingDocumentUrl(null);
    }
  };

  // Format address
  const formatAddress = (storeData) => {
    if (!storeData) return "N/A";
    const parts = [];
    if (storeData.address) parts.push(storeData.address);
    if (storeData.address_line2) parts.push(storeData.address_line2);
    if (storeData.city || storeData.state || storeData.zip) {
      const cityStateZip = [
        storeData.city,
        storeData.state ? `${storeData.state} ${storeData.zip || ""}`.trim() : storeData.zip
      ].filter(Boolean).join(", ");
      if (cityStateZip) parts.push(cityStateZip);
    }
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  return (
    <>
      <ModalBasic
        width={"5xl"}
        modalOpen={Boolean(store)}
        setModalOpen={() => setModalOpen(null)}
        title={`Prospect Review - ${store.name}`}
      >
        <div className="space-y-6">
          {/* Store Information */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Store Information</h3>
            {loadingDetails ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Loading store details...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600 dark:text-slate-400">Store ID:</span>
                  <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">{displayStore.store_id}</span>
                </div>
                <div>
                  <span className="text-slate-600 dark:text-slate-400">Email:</span>
                  <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">{displayStore.email}</span>
                </div>
                <div>
                  <span className="text-slate-600 dark:text-slate-400">Phone:</span>
                  <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                    {displayStore.phone ? formatPhoneNumber(displayStore.phone) : "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-600 dark:text-slate-400">Created:</span>
                  <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                    {displayStore.created ? new Date(displayStore.created).toLocaleDateString() : "N/A"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Publishing Preference */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Publishing Preference</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-600 dark:text-slate-400">Publish Option:</span>
                <span className="ml-2 font-medium text-slate-800 dark:text-slate-100 capitalize">
                  {publishOption !== 'N/A' ? publishOption : 'Not set'}
                </span>
              </div>
              {publishOption === 'schedule' && scheduledDate && (
                <div>
                  <span className="text-slate-600 dark:text-slate-400">Scheduled Date:</span>
                  <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                    {new Date(scheduledDate).toLocaleDateString()}
                  </span>
                </div>
              )}
              {publishOption === 'schedule' && scheduledTime && (
                <div>
                  <span className="text-slate-600 dark:text-slate-400">Scheduled Time:</span>
                  <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                    {scheduledTime}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Overall Status */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">Overall Status</h3>
              <div className="flex items-center gap-3">
                {overallStatus === 'submitted' && <Badge color="yellow">Submitted for Review</Badge>}
                {overallStatus === 'rejected' && <Badge color="red">Rejected</Badge>}
                {overallStatus === 'scheduled_publish' && <Badge color="green">Scheduled Publish</Badge>}
                {overallStatus === 'awaiting_manual_publish' && <Badge color="green">Awaiting Manual Publish</Badge>}
                {(!overallStatus || overallStatus === 'working') && <Badge color="blue">In Progress</Badge>}
              </div>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Progress: {completedSteps} / {totalSteps} completed ({inProgressSteps} in progress)
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-500 dark:bg-indigo-400 transition-all duration-300"
              style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
            />
          </div>

          {/* Onboard Steps */}
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Onboard Steps</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {onboardChecklistItems.map((item) => {
                const status = stepsStatus[item.key];
                const prerequisiteItem = item.prerequisite ? onboardChecklistItems.find(i => i.key === item.prerequisite) : null;
                const prerequisiteStatus = item.prerequisite ? stepsStatus[item.prerequisite] : null;
                const prerequisiteMet = !item.prerequisite || prerequisiteStatus === 'completed';
                const isBusinessDetails = item.key === 'editBusinessDetails';
                const isUploadDocuments = item.key === 'uploadDocuments';
                const accordionKey = isBusinessDetails ? 'businessDetails' : isUploadDocuments ? 'businessDocuments' : null;
                const isAccordionOpen = accordionKey ? expandedAccordions[accordionKey] : false;

                return (
                  <div
                    key={item.key}
                    className={`flex flex-col gap-0 border rounded-lg ${
                      status === 'completed'
                        ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                        : status === 'in_progress'
                        ? 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (isBusinessDetails || isUploadDocuments) {
                          setExpandedAccordions(prev => ({
                            ...prev,
                            [accordionKey]: !prev[accordionKey]
                          }));
                        }
                      }}
                      className={`flex items-start gap-3 p-3 w-full text-left ${
                        (isBusinessDetails || isUploadDocuments) 
                          ? 'cursor-pointer hover:bg-opacity-80' 
                          : 'cursor-default'
                      }`}
                      disabled={!isBusinessDetails && !isUploadDocuments}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getStepStatusIcon(item.key)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                            {item.label}
                          </div>
                          <div className="flex items-center gap-2">
                            {getStepStatusBadge(item.key)}
                            {(isBusinessDetails || isUploadDocuments) && (
                              <svg 
                                className={`w-4 h-4 text-slate-400 transition-transform ${isAccordionOpen ? 'rotate-180' : ''}`} 
                                viewBox="0 0 32 32"
                                fill="currentColor"
                              >
                                <path d="M16 20l-5.4-5.4 1.4-1.4 4 4 4-4 1.4 1.4z" />
                              </svg>
                            )}
                          </div>
                        </div>
                        {item.description && (
                          <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                            {item.description}
                          </div>
                        )}
                        {item.prerequisite && !prerequisiteMet && (
                          <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            Prerequisite: {prerequisiteItem?.label} (Not completed)
                          </div>
                        )}
                      </div>
                    </button>
                    
                    {/* Business Details Accordion */}
                    {isBusinessDetails && isAccordionOpen && (
                      <div className="px-3 pb-3 border-t border-slate-200 dark:border-slate-600 mt-2 pt-3">
                        <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-3">Business Details</h4>
                        {loadingDetails ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                            <p className="text-xs text-slate-600 dark:text-slate-400">Loading business details...</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-3 text-xs bg-white dark:bg-slate-700 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                            <div>
                              <span className="text-slate-600 dark:text-slate-400">Legal Business Name:</span>
                              <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                                {displayStore.legal_name || "N/A"}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-600 dark:text-slate-400">Business Name (DBA):</span>
                              <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                                {displayStore.name || "N/A"}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-600 dark:text-slate-400">EIN:</span>
                              <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                                {displayStore.ein || "N/A"}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-600 dark:text-slate-400">Business Address:</span>
                              <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                                {formatAddress(displayStore)}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-600 dark:text-slate-400">Business Phone Number:</span>
                              <span className="ml-2 font-medium text-slate-800 dark:text-slate-100">
                                {displayStore.business_phone ? formatPhoneNumber(displayStore.business_phone) : (displayStore.phone ? formatPhoneNumber(displayStore.phone) : "N/A")}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Business Documents Accordion */}
                    {isUploadDocuments && isAccordionOpen && (
                      <div className="px-3 pb-3 border-t border-slate-200 dark:border-slate-600 mt-2 pt-3">
                        <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-3">Business Documents</h4>
                        {loadingDocuments ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                            <p className="text-xs text-slate-600 dark:text-slate-400">Loading documents...</p>
                          </div>
                        ) : businessDocuments.length === 0 ? (
                          <div className="text-xs text-slate-600 dark:text-slate-400 text-center py-4 bg-white dark:bg-slate-700 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                            No documents uploaded yet.
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {(businessDocuments||[]).map((doc) => (
                              <div
                                key={doc.id}
                                className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-2 flex items-center gap-2"
                              >
                                <div className="w-8 h-8 bg-slate-100 dark:bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                  {getFileIcon(doc.original_name || doc.filename || doc.url, "w-4 h-4")}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-medium text-slate-800 dark:text-slate-100 truncate text-xs">
                                    {doc.original_name || doc.filename || "Document"}
                                  </h5>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {doc.document_type || "Document"} • {doc.created_at || doc.created ? new Date(doc.created_at || doc.created).toLocaleDateString() : "Unknown date"}
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleViewDocument(doc)}
                                  disabled={loadingDocumentUrl === doc.id}
                                  className="p-1.5 text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="View document"
                                >
                                  {loadingDocumentUrl === doc.id ? (
                                    <svg className="animate-spin w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <FaArrowUpRightFromSquare className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          {showActions && (
            <div className="flex gap-2 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
              {/* <SecondaryButton
                onClick={handleArchive}
                disabled={archiving || processing}
              >
                {archiving ? "Archiving..." : "Archive Store"}
              </SecondaryButton> */}
              {canReject && (
                <DangerButton
                  onClick={() => setShowRejectModal(true)}
                  disabled={processing || archiving}
                >
                  Reject
                </DangerButton>
              )}
              {canApprove && (
                <PrimaryButton
                  onClick={handleApproveClick}
                  disabled={processing || archiving}
                >
                  Approve
                </PrimaryButton>
              )}
            </div>
          )}
        </div>
      </ModalBasic>

      {/* Approve Confirmation Modal */}
      <ModalBasic
        width={"md"}
        modalOpen={showApproveModal}
        setModalOpen={setShowApproveModal}
        title="Approve Onboard Steps"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Are you sure you want to approve the onboard steps for <span className="font-semibold text-slate-800 dark:text-slate-100">{store.name}</span>?
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Once approved, the store will be published according to the merchant's selected publishing preference.
          </p>
          <div className="flex gap-2 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton
              onClick={() => setShowApproveModal(false)}
              disabled={processing}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleConfirmApprove}
              disabled={processing}
              loading={processing}
            >
              {processing ? "Approving..." : "Approve"}
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>

      {/* Reject Modal */}
      <ModalBasic
        width={"md"}
        modalOpen={showRejectModal}
        setModalOpen={setShowRejectModal}
        title="Reject Onboard Steps"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Please provide a reason for rejection. This will be emailed to the user.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              className="form-textarea w-full"
              rows={6}
              placeholder="Enter the reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton
              onClick={() => {
                setShowRejectModal(false);
                setRejectionReason("");
              }}
              disabled={processing}
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              onClick={handleReject}
              disabled={processing || !rejectionReason.trim()}
            >
              {processing ? "Rejecting..." : "Reject & Email User"}
            </DangerButton>
          </div>
        </div>
      </ModalBasic>
    </>
  );
}

function StoresTable({
  stores,
  dspProviders,
  getMerchants,
  markets,
  teams,
  storeType,
  loading = false,
  emptyMessage = "No stores to show.",
}) {
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [showArchive, setShowArchive] = useState(null);
  const [showManualPayout, setShowManualPayout] = useState(null);
  const [showRequestLicenses, setShowRequestLicenses] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [showProspectProgress, setShowProspectProgress] = useState(null);
  const [showMarketplaceUpload, setShowMarketplaceUpload] = useState(false);
  const isMarketplaceTab =
    storeType === "marketplace" || storeType === "marketplace-onboarding";
  const dispatch = useDispatch();
  if (!stores) {
    return;
  }
  const handleClick = (id) => {
    setShowStoreModal(id);
  };
  const prevOrg = useRef(null);
  return (
    <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
      {storeType === "direct" && (
        <header className="px-5 py-4">
          <h2 className="font-semibold text-slate-800 dark:text-slate-100">
            <PrimaryButton
              onClick={() => {
                setShowStoreModal("new");
              }}
            >
              Create new Store
            </PrimaryButton>
          </h2>
        </header>
      )}
      {isMarketplaceTab && (
        <header className="px-5 py-4">
          <PrimaryButton onClick={() => setShowMarketplaceUpload(true)}>
            Bulk Upload Sellers (CSV)
          </PrimaryButton>
        </header>
      )}
      <MarketplaceCSVUpload
        modalOpen={showMarketplaceUpload}
        setModalOpen={setShowMarketplaceUpload}
        onComplete={() => getMerchants && getMerchants()}
      />
      <div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-slate-300">
            {/* Table header */}
            <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Store ID</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Store Name</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Email</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Phone</div>
                </th>
                {storeType !== "direct prospects" && (
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      Payment Capability
                    </div>
                  </th>
                )}
                {storeType !== "direct prospects" && ( 
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Enabled</div>
                  </th>
                )}
                {storeType === "direct prospects" && (
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      Status
                    </div>
                  </th>
                )}
                {storeType === "direct prospects" && (
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Created At</div>
                  </th>
                )}
                {storeType === "marketplace-onboarding" && (
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Step</div>
                  </th>
                )}
                {storeType === "marketplace-onboarding" && (
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Last Step Completed</div>
                  </th>
                )}
              </tr>
            </thead>
            {/* Table body */}
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-2 first:pl-5 last:pr-5 py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Loading stores...</p>
                    </div>
                  </td>
                </tr>
              ) : stores.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                stores.map((store) => {
                  if (prevOrg && store.org && prevOrg.current !== store.org && (storeType==='direct' || storeType==='direct prospects')) {
                    prevOrg.current = store.org;
                    return (
                      <React.Fragment key={store.org}>
                        <tr
                          key={store.org}
                          className="bg-slate-50 dark:bg-slate-900/20"
                        >
                          <td
                            colSpan="8"
                            className="px-2 first:pl-5 last:pr-5 py-1 whitespace-nowrap"
                          >
                            <div className="font-semibold text-xs text-left">
                              {store.account}
                            </div>
                          </td>
                        </tr>
                        <StoreTableItem
                          store={store}
                          key={store.id}
                          handleClick={handleClick}
                          setShowArchive={setShowArchive}
                          setShowManualPayout={setShowManualPayout}
                          setShowRequestLicenses={setShowRequestLicenses}
                          storeType={storeType}
                          showProspectProgress={showProspectProgress}
                          setShowProspectProgress={(store) => setShowProspectProgress(store)}
                          refreshStores={getMerchants}
                        />
                      </React.Fragment>
                    );
                  }
                  return (
                    <StoreTableItem
                      store={store}
                      key={store.id}
                      handleClick={handleClick}
                      setShowArchive={setShowArchive}
                      setShowManualPayout={setShowManualPayout}
                      setShowRequestLicenses={setShowRequestLicenses}
                      storeType={storeType}
                      showProspectProgress={showProspectProgress}
                      setShowProspectProgress={setShowProspectProgress}
                      refreshStores={getMerchants}
                    />
                  );
                })
              )}
            </tbody>
          </table>
          <StoreModal
            markets={markets}
            teams={teams}
            id={showStoreModal}
            setStoreID={setShowStoreModal}
            dspProviders={dspProviders}
            closeModal={() => {
              setShowStoreModal(null);
            }}
            triggerGetMerchants={getMerchants}
            storeType={storeType}
          />
        </div>
      </div>
      <ProspectProgressModal
        store={showProspectProgress}
        setModalOpen={setShowProspectProgress}
        getMerchants={getMerchants}
      />
      <SellerManualPayoutModal
        store={showManualPayout}
        setModalOpen={setShowManualPayout}
      />
      <RequestLicensesModal
        store={showRequestLicenses}
        setModalOpen={setShowRequestLicenses}
      />
      <ModalBasic
        width={"md"}
        modalOpen={Boolean(showArchive)}
        setModalOpen={setShowArchive}
        title={"Are you sure"}
      >
        <div className="text-sm space-y-4">
          <p>
            Are you sure you want to archive{" "}
            <span className="font-bold">{showArchive?.name}</span>?
          </p>
          <p>
            The store will cease to function and merchant will not be able to
            login to this store. Any pending charge will be settled and their
            subscription license will end at the end of this billing cycle.
          </p>
        </div>
        <div className={"flex gap-2 justify-end"}>
          <SecondaryButton
            type="button"
            loading={archiving}
            onClick={(e) => {
              e.preventDefault();
              setShowArchive(null);
            }}
          >
            Cancel
          </SecondaryButton>
          <DangerButton
            onClick={async (e) => {
              setArchiving(true);
              try {
                await archiveStore(showArchive.id);
                getMerchants();
                dispatch(
                  addToast({
                    type: "success",
                    message: `Succesfully archived ${showArchive.name}`,
                    timer: 5,
                  })
                );
              } catch (e) {
                dispatch(
                  addToast({
                    type: "error",
                    message: `Failed to archive ${showArchive.name}`,
                  })
                );
              } finally {
                setArchiving(false);
                setShowArchive(null);
              }
            }}
            loading={archiving}
          >
            Archive
          </DangerButton>
        </div>
      </ModalBasic>
    </div>
  );
}

export default StoresTable;
